import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import ElevatorIcon from '@mui/icons-material/Elevator';
import HandymanIcon from '@mui/icons-material/Handyman';
import InboxIcon from '@mui/icons-material/Inbox';
import MenuIcon from '@mui/icons-material/Menu';
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import {
  Badge,
  Box,
  ButtonBase,
  Divider,
  Drawer,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@mui/material';
import * as React from 'react';
import { useContext } from 'react';
import { useLocation } from 'react-router';

import { UserContext } from '~/auth/userContext';
import { DemoBanner } from '~/components/common/DemoBanner';
import { ImpersonationBanner } from '~/components/common/ImpersonationBanner';
import { publishSidebarWidth, resetSidebarWidth } from '~/components/common/sidebarWidth';
import { AI_EXTRACTED_ROUTE } from '~/components/documentReview/routes';
import { AppBar } from '~/components/global';
import { APP_BAR_HEIGHT } from '~/components/global/appBarHeight';
import { appliedGlobalFilter } from '~/components/global/globalFilterParams';
import { ContractIcon, InvoiceIcon, PortfolioIcon, TicketIcon } from '~/components/icons';
import { AddKnowledgeRailButton } from '~/components/knowledge/AddKnowledgeRailButton';
import UploadDocumentButton from '~/components/uploadDocument/UploadDocumentButton';
import { useGlobalFilterContext } from '~/contexts/globalFilterContext';
import { WORK_QUEUE_POLL_MS } from '~/data/hooks/useActionItems';
import { useInboxActionItems } from '~/data/hooks/useInboxActionItems';
import type { UserRole } from '~/data/schemas/user';
import { LocaleTerms, useLocaleTerms } from '~/hooks/useLocaleTerms';
import { BUTTON_RADIUS } from '~/theme';
import { useFeatureFlags } from '~/utils/featureFlags';
import { canAccess, CAPTURE_KNOWLEDGE, DOCUMENT_REVIEW, UPLOAD_DOCUMENT_FORM } from '~/utils/role';

// Build's git commit SHA, injected by Vite's `define` (see vite.config.ts).
// Guarded with `typeof` so environments without the define (e.g. some test
// setups) fall back cleanly.
const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev';

const EXPANDED_WIDTH = 212;
const COLLAPSED_WIDTH = 64;
const COLLAPSE_STORAGE_KEY = 'liftai.sidebar.collapsed';

interface SideBarLayoutProps {
  routeMatches: (path: string) => boolean;
  children: React.ReactNode;
  openSearch: () => void;
}

const DEFAULT_ROUTE = '/' as const;
export const PORTFOLIO_ROUTE = '/portfolio' as const;
// The property table, split out of `/portfolio`'s Table tab.
export const PROPERTIES_ROUTE = '/properties' as const;
export const TICKETS_ROUTE = '/tickets' as const;
export const SPENDING_ROUTE = '/spending' as const;
export const COMPLIANCE_ROUTE = '/compliance' as const;
export const CONTRACTS_ROUTE = '/contracts' as const;
export const ADMIN_ROUTE = '/admin' as const;
export const TOOLS_ROUTE = '/tools' as const;
// AI_EXTRACTED_ROUTE (/ai-extracted, the consultant-facing home for the AI
// document pipeline) lives in ~/components/documentReview/routes so this nav
// logic and useDocReviewBase share one source of truth. Re-exported here for
// existing importers.
export { AI_EXTRACTED_ROUTE };
export const REPORTS_ROUTE = '/reports' as const;
export const WORK_QUEUE_ROUTE = '/work-queue' as const;
export const TASKS_ROUTE = '/tasks' as const;

type VALID_ROUTE =
  | typeof DEFAULT_ROUTE
  | typeof PORTFOLIO_ROUTE
  | typeof PROPERTIES_ROUTE
  | typeof TICKETS_ROUTE
  | typeof SPENDING_ROUTE
  | typeof COMPLIANCE_ROUTE
  | typeof CONTRACTS_ROUTE
  | typeof ADMIN_ROUTE
  | typeof TOOLS_ROUTE
  | typeof REPORTS_ROUTE
  | typeof WORK_QUEUE_ROUTE
  | typeof TASKS_ROUTE;

// Clients reached the property table through `/portfolio`'s Table tab, so they have to
// be granted the route it moved to or they lose the table.
const ALLOWED_ROUTES: Record<NonNullable<UserRole>, VALID_ROUTE[]> = {
  client: [PORTFOLIO_ROUTE, PROPERTIES_ROUTE, TICKETS_ROUTE, CONTRACTS_ROUTE, REPORTS_ROUTE],
  consultant: [
    WORK_QUEUE_ROUTE,
    TASKS_ROUTE,
    PORTFOLIO_ROUTE,
    PROPERTIES_ROUTE,
    TICKETS_ROUTE,
    SPENDING_ROUTE,
    COMPLIANCE_ROUTE,
    CONTRACTS_ROUTE,
    REPORTS_ROUTE,
  ],
  service_provider: [DEFAULT_ROUTE],
  none: [REPORTS_ROUTE],
};

export type MenuItem = {
  label: string;
  icon: React.ReactNode;
  pathname: string;
};

export type MenuGroup = {
  /** `null` renders the items with no header — see `visibleMenuGroups`. */
  label: string | null;
  items: MenuItem[];
};

/**
 * The nav split into sections, in render order. The cut is by what you came to do:
 * "Overview" holds the derived views, "Records" the lists you open to find and act on
 * one thing, "Internal" the operator surfaces. Needs Attention sits above both with no header,
 * being the priority action rather than a category.
 */
// NOTE: Using a partial path autuomatically keeps query params
export const getMainMenuGroups = (terms: LocaleTerms): MenuGroup[] => [
  {
    label: null,
    items: [
      {
        label: 'Needs Attention',
        icon: <InboxIcon />,
        pathname: WORK_QUEUE_ROUTE,
      },
      {
        label: 'Custom Tasks',
        icon: <TaskAltIcon />,
        pathname: TASKS_ROUTE,
      },
    ],
  },
  {
    label: 'Overview',
    items: [
      {
        label: 'Portfolios',
        icon: <SpaceDashboardOutlinedIcon />,
        pathname: PORTFOLIO_ROUTE,
      },
      {
        label: 'Insights',
        icon: <AutoAwesomeIcon />,
        pathname: REPORTS_ROUTE,
      },
    ],
  },
  {
    label: 'Records',
    items: [
      {
        label: 'Properties',
        icon: <PortfolioIcon />,
        pathname: PROPERTIES_ROUTE,
      },
      {
        label: 'Tickets',
        icon: <TicketIcon />,
        pathname: TICKETS_ROUTE,
      },
      {
        label: 'Spendings',
        icon: <InvoiceIcon />,
        pathname: SPENDING_ROUTE,
      },
      {
        label: 'Contracts',
        icon: <ContractIcon />,
        pathname: CONTRACTS_ROUTE,
      },
      {
        label: terms.elevators,
        icon: <ElevatorIcon />,
        pathname: COMPLIANCE_ROUTE,
      },
    ],
  },
  {
    // Its own header rather than the foot of Records: a processing queue you work
    // through, not one of the domain lists you look things up in.
    label: 'Documents',
    items: [
      {
        // Consultant-facing entry to the document-processing review queue. Superusers
        // reach the same portal through the operator "Tools" menu, so this item is
        // hidden from them (see menuGuardPredicate) to avoid a duplicate.
        label: 'AI Pipeline Uploads',
        icon: <UploadFileOutlinedIcon />,
        pathname: AI_EXTRACTED_ROUTE,
      },
    ],
  },
  {
    label: 'Internal',
    items: [
      {
        label: 'Admin',
        icon: <AdminPanelSettingsIcon />,
        pathname: ADMIN_ROUTE,
      },
      {
        label: 'Tools',
        icon: <HandymanIcon />,
        pathname: TOOLS_ROUTE,
      },
    ],
  },
];

/**
 * Applies the per-item access rules to each group and drops the groups that end up
 * empty, so nobody sees an "Internal" header with nothing under it. `showLabels` is
 * off for clients, who see too few links for headers to be worth the chrome.
 */
export const visibleMenuGroups = (
  groups: MenuGroup[],
  isVisible: (item: MenuItem) => boolean,
  showLabels: boolean,
): MenuGroup[] =>
  groups
    .map((group) => ({
      label: showLabels ? group.label : null,
      items: group.items.filter(isVisible),
    }))
    .filter((group) => group.items.length > 0);

type SideBarRowProps = {
  icon: React.ReactNode;
  label: string;
  collapsed: boolean;
  active?: boolean;
  href?: string;
  onClick?: React.MouseEventHandler;
  testId?: string;
  // Optional element pinned to the right of the row (expanded only), e.g. a
  // count pill. Hidden in the collapsed rail where there's no room.
  trailing?: React.ReactNode;
};

// Shared presentational row for both navigation links and bottom actions.
// In the collapsed rail it renders icon-only and surfaces the label via a
// tooltip; expanded it shows the icon + label side by side.
const SideBarRow = ({
  icon,
  label,
  collapsed,
  active = false,
  href,
  onClick,
  testId,
  trailing,
}: SideBarRowProps) => {
  const row = (
    <Box
      component={href ? Link : 'button'}
      href={href}
      onClick={onClick}
      data-testid={testId}
      data-active={active}
      aria-label={label}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        width: '100%',
        minHeight: 34,
        px: collapsed ? 0 : 1.25,
        justifyContent: collapsed ? 'center' : 'flex-start',
        borderRadius: `${BUTTON_RADIUS}px`,
        border: 'none',
        cursor: 'pointer',
        textAlign: 'left',
        textDecoration: 'none',
        fontWeight: active ? 600 : 500,
        color: active ? 'primary.main' : 'text.secondary',
        backgroundColor: active ? 'primary.light' : 'transparent',
        '&:hover': { backgroundColor: active ? 'primary.light' : 'action.hover' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: 'inherit',
          '& .MuiSvgIcon-root': { fontSize: 20 },
        }}
      >
        {icon}
      </Box>
      <Typography
        component="span"
        variant="body2"
        sx={{
          fontWeight: 'inherit',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: collapsed ? 'none' : 'block',
        }}
      >
        {label}
      </Typography>
      {!collapsed && trailing ? (
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>{trailing}</Box>
      ) : null}
    </Box>
  );

  return collapsed ? (
    <Tooltip title={label} placement="right">
      {row}
    </Tooltip>
  ) : (
    row
  );
};

const SideBarNavItem = ({
  icon,
  label,
  pathname,
  collapsed,
  active,
  onClick,
  trailing,
}: MenuItem & {
  collapsed: boolean;
  active: boolean;
  onClick?: React.MouseEventHandler;
  trailing?: React.ReactNode;
}) => {
  return (
    <SideBarRow
      icon={icon}
      label={label}
      collapsed={collapsed}
      active={active}
      href={pathname}
      onClick={onClick}
      testId="app-bar-button"
      trailing={trailing}
    />
  );
};

// Backlog shows the open (non-snoozed) count for the current global Portfolio /
// Region scope, the same scope the Work Queue list uses, so the badge and the page
// can't disagree. Its own component so the count fetch only runs for roles that see
// the item; collapsed to the icon rail there is no room for a pill, so it falls back
// to an icon badge.
const BacklogNavItem = (
  props: MenuItem & { collapsed: boolean; active: boolean; onClick?: React.MouseEventHandler },
) => {
  const { options, selectedFilters } = useGlobalFilterContext();
  const portfolio = appliedGlobalFilter(selectedFilters.portfolios, options.portfolios);
  const region = appliedGlobalFilter(selectedFilters.regions, options.regions);

  // Poll so the badge reflects items created by background processing (a
  // document becoming ready, an upload reaching split review) without waiting
  // for the consultant to open the page.
  const { count } = useInboxActionItems(
    { ...(portfolio && { portfolio }), ...(region && { region }) },
    { refreshInterval: WORK_QUEUE_POLL_MS },
  );

  if (props.collapsed) {
    return (
      <SideBarNavItem
        {...props}
        icon={
          <Badge badgeContent={count} max={9} color="error" invisible={count === 0}>
            {props.icon}
          </Badge>
        }
      />
    );
  }

  return (
    <SideBarNavItem
      {...props}
      trailing={
        count > 0 ? (
          <Box
            component="span"
            sx={{
              minWidth: 22,
              px: 0.75,
              height: 20,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 1,
              fontSize: 12,
              fontWeight: 600,
              color: 'error.contrastText',
              bgcolor: 'error.main',
            }}
          >
            {count > 99 ? '99+' : count}
          </Box>
        ) : null
      }
    />
  );
};

function SideBarLayout({ children, openSearch, routeMatches }: SideBarLayoutProps) {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(COLLAPSE_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  // Published for the surfaces that sit beside the rail rather than inside the content
  // column — the full-mode Lift AI panel — which can't assume a width now that the
  // sidebar collapses.
  React.useEffect(() => {
    publishSidebarWidth(collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH);
    return resetSidebarWidth;
  }, [collapsed]);

  const location = useLocation();
  const isToolsPage = location.pathname.startsWith(TOOLS_ROUTE);

  const {
    userDetails: { role: userRole, isLiftaiAdmin, isGroupAdmin, clientGroup },
  } = useContext(UserContext);

  const featureFlags = useFeatureFlags();
  const terms = useLocaleTerms();

  function menuGuardPredicate({ pathname }: MenuItem): boolean {
    // Work Queue is behind the `work-queue` LaunchDarkly flag (LD key
    // `work-queue` → `featureFlags.workQueue`) during rollout. Hidden until the
    // flag is enabled for the user (and while LD is still initializing).
    if (pathname === WORK_QUEUE_ROUTE) {
      return ALLOWED_ROUTES[userRole].includes(pathname) && featureFlags.workQueue === true;
    }

    if (pathname === TASKS_ROUTE) {
      return (
        ALLOWED_ROUTES[userRole].includes(pathname) &&
        featureFlags.tasks === true &&
        canAccess(userRole, CAPTURE_KNOWLEDGE)
      );
    }

    // Special handling for Insights route
    if (pathname === REPORTS_ROUTE) {
      return ALLOWED_ROUTES[userRole].includes(pathname) && featureFlags.liftAiInsights === true;
    }

    // Special handling for Spending route - clients need feature flag
    if (pathname === SPENDING_ROUTE) {
      if (userRole === 'client' && clientGroup) {
        return featureFlags.clientPortalSpending === true;
      }
      return ALLOWED_ROUTES[userRole].includes(pathname);
    }

    // Admin route - visible only to group admins
    if (pathname === ADMIN_ROUTE) {
      return !!isGroupAdmin;
    }

    // AI Pipeline - dedicated consultant entry. Superusers use the Tools
    // menu (which contains the same portal), so hide it from them.
    if (pathname === AI_EXTRACTED_ROUTE) {
      return canAccess(userRole, DOCUMENT_REVIEW) && !isLiftaiAdmin;
    }

    return (
      ALLOWED_ROUTES[userRole].includes(pathname as VALID_ROUTE) ||
      (pathname === TOOLS_ROUTE && isLiftaiAdmin)
    );
  }

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(COLLAPSE_STORAGE_KEY, String(next));
      } catch {
        // Ignore storage failures (private mode, quota); collapse still works
        // for the session.
      }
      return next;
    });
  };

  // The reports relaunch renames the Insights section to "Reports" (and swaps its
  // sparkle icon for a report/chart one) behind the LD flag (LD key created in
  // AMP-649 → `featureFlags.reportsRelaunch`).
  const mainMenuGroups = getMainMenuGroups(terms).map((group) => ({
    ...group,
    items: group.items.map((item) =>
      item.pathname === REPORTS_ROUTE && featureFlags.reportsRelaunch === true
        ? { ...item, label: 'Reports', icon: <AssessmentIcon /> }
        : item,
    ),
  }));

  // Clients get the same links rendered flat, without section headers.
  const visibleGroups = visibleMenuGroups(
    mainMenuGroups,
    menuGuardPredicate,
    userRole !== 'client',
  );

  // `isCollapsed` is the desktop collapse state; the temporary (mobile) drawer
  // always renders expanded since the rail toggle isn't shown there. `onNavigate`
  // (mobile only) dismisses the overlay drawer once the user picks a destination
  // so it doesn't stay open covering the page.
  const renderDrawer = (isCollapsed: boolean, collapsible: boolean, onNavigate?: () => void) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Takes the global header's height so the logo lines up with the bar beside it. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: isCollapsed ? 0.5 : 1.25,
          minHeight: APP_BAR_HEIGHT,
        }}
      >
        <ButtonBase
          href="/"
          aria-label="AdvisorLIFT home"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            flexGrow: 1,
            justifyContent: isCollapsed ? 'center' : 'flex-start',
            borderRadius: 1,
            minWidth: 0,
          }}
        >
          <img
            src="/images/lift-logo-small.svg"
            alt="AdvisorLIFT"
            style={{ height: 28, width: 28 }}
          />
          {!isCollapsed && (
            <Box sx={{ textAlign: 'left', minWidth: 0 }}>
              <Typography
                component="span"
                sx={{ display: 'block', fontSize: 16, lineHeight: 1.15, letterSpacing: '-0.01em' }}
              >
                <Box component="span" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Advisor
                </Box>
                <Box component="span" sx={{ fontWeight: 800, color: 'primary.main' }}>
                  LIFT
                </Box>
              </Typography>
            </Box>
          )}
        </ButtonBase>
        {collapsible && !isCollapsed ? (
          <Tooltip title="Collapse" placement="right">
            <IconButton size="small" aria-label="Collapse sidebar" onClick={toggleCollapsed}>
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>
        ) : null}
        {/* Mobile drawer: explicit close affordance (no rail toggle here). */}
        {!collapsible && onNavigate ? (
          <IconButton aria-label="Close navigation menu" onClick={onNavigate}>
            <CloseIcon />
          </IconButton>
        ) : null}
      </Box>

      {collapsible && isCollapsed ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pb: 0.5 }}>
          <Tooltip title="Expand" placement="right">
            <IconButton size="small" aria-label="Expand sidebar" onClick={toggleCollapsed}>
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ) : null}

      {canAccess(userRole, UPLOAD_DOCUMENT_FORM) ? (
        <Box sx={{ px: 0.75, pt: 0.5, pb: 1.75 }}>
          <UploadDocumentButton variant="rail" collapsed={isCollapsed} />
        </Box>
      ) : null}
      <AddKnowledgeRailButton collapsed={isCollapsed} />

      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          px: 0.75,
          pt: 0.75,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.25,
        }}
      >
        {visibleGroups.map((group, groupIndex) => (
          <Box
            key={group.label ?? `group-${groupIndex}`}
            sx={{ display: 'flex', flexDirection: 'column' }}
          >
            {group.label && !isCollapsed ? (
              <Typography
                variant="caption"
                sx={{
                  px: 1.25,
                  pt: groupIndex === 0 ? 0.25 : 1,
                  pb: 0.25,
                  color: 'text.disabled',
                  fontWeight: 700,
                  fontSize: 10,
                  lineHeight: 1.4,
                  letterSpacing: '0.08em',
                }}
              >
                {group.label.toUpperCase()}
              </Typography>
            ) : null}
            {group.label && isCollapsed && groupIndex > 0 ? <Divider sx={{ my: 0.5 }} /> : null}
            {group.items.map((item) => {
              // `routeMatches` is a top-level matcher (see `useTopLevelRouteMatch` in _app.tsx), so
              // a nested route like /properties/:id still highlights its parent section — which
              // `useMatch(pathname)` would not.
              const NavItem = item.pathname === WORK_QUEUE_ROUTE ? BacklogNavItem : SideBarNavItem;
              return (
                <NavItem
                  key={item.label}
                  {...item}
                  collapsed={isCollapsed}
                  active={routeMatches(item.pathname)}
                  onClick={onNavigate}
                />
              );
            })}
          </Box>
        ))}
      </Box>

      {/* On screen, not in the account menu: a problem report arrives as a screenshot,
          and this is how we learn which build it was taken on. */}
      <Box sx={{ mt: 'auto' }}>
        <Divider sx={{ mx: 0.75 }} />
        <Tooltip title={isCollapsed ? `Version: ${APP_VERSION}` : ''} placement="right">
          <Typography
            component="div"
            sx={{
              px: isCollapsed ? 0.5 : 1.75,
              py: 1,
              fontSize: 10,
              lineHeight: 1.3,
              color: 'text.secondary',
              fontFamily: 'monospace',
              textAlign: isCollapsed ? 'center' : 'left',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {isCollapsed ? APP_VERSION : `Version: ${APP_VERSION}`}
          </Typography>
        </Tooltip>
      </Box>
    </Box>
  );

  const desktopWidth = collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%', maxHeight: '100%' }}>
      <DemoBanner />
      <ImpersonationBanner />
      {/* Tools pages render no AppBar, so they'd otherwise have no way to open
          the navigation drawer on mobile. Provide a standalone hamburger. */}
      {isToolsPage ? (
        <IconButton
          aria-label="Open navigation menu"
          onClick={handleDrawerToggle}
          sx={{
            display: { xs: 'inline-flex', sm: 'none' },
            position: 'fixed',
            top: 8,
            left: 8,
            zIndex: (theme) => theme.zIndex.drawer + 3,
            backgroundColor: 'background.paper',
            boxShadow: 2,
            '&:hover': { backgroundColor: 'background.paper' },
          }}
        >
          <MenuIcon />
        </IconButton>
      ) : null}
      {!isToolsPage && (
        <Box
          sx={{
            // `xs: '100%'` is required: the AppBar is `position: fixed; right: 0`
            // with `width: inherit`, so without a definite width here it shrinks
            // to its content and clings to the right edge on mobile.
            width: { xs: '100%', sm: `calc(100% - ${desktopWidth}px)` },
            ml: { sm: `${desktopWidth}px` },
            transition: (theme) =>
              theme.transitions.create(['width', 'margin'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
          }}
        >
          <AppBar openSearch={openSearch} onMenuClick={handleDrawerToggle} />
        </Box>
      )}
      <Box
        component="nav"
        sx={{
          width: { sm: desktopWidth },
          flexShrink: { sm: 0 },
          zIndex: (theme) => theme.zIndex.drawer + 2,
        }}
        aria-label="primary navigation"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            // Sit above the AppBar (which is `zIndex.drawer + 1`) so the open
            // drawer is a clean full-height overlay with its scrim covering the
            // top bar — otherwise the AppBar paints over the drawer's header.
            zIndex: (theme) => theme.zIndex.modal,
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: EXPANDED_WIDTH,
              overflowX: 'hidden',
            },
          }}
        >
          {renderDrawer(false, false, handleDrawerToggle)}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: desktopWidth,
              overflowX: 'hidden',
              borderRight: '1px solid',
              borderColor: 'divider',
              transition: (theme) =>
                theme.transitions.create('width', {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
            },
          }}
          open
        >
          {renderDrawer(collapsed, true)}
        </Drawer>
      </Box>
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexGrow: 1,
          width: { sm: `calc(100% - ${desktopWidth}px)` },
          ml: { sm: `${desktopWidth}px` },
          transition: (theme) =>
            theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
        }}
      >
        {children}
      </Box>
      {/* Mounted once here, not inside `renderDrawer` — that block is rendered twice
          (temporary + permanent Drawer), which would mount the dialog twice. */}
    </Box>
  );
}

export default SideBarLayout;
